/**
 * Kunden — die Auftraggeber.
 *
 * Aufgebaut wie die Orte, aus demselben Grund: Auch Kundennamen laufen
 * auseinander, sobald jemand sie tippt statt auswählt. Zusammenführen und
 * „Meinst du eventuell …" gibt es hier deshalb genauso.
 *
 * Ein Unterschied zu den Orten: Das Rechnungsintervall steht in der Zeile und
 * ist dort auch änderbar. Es entscheidet später, wie die Abrechnung bündelt —
 * wöchentlich ist die Regel bei Haid & Jerg, aber nicht bei jedem.
 *
 * SevDesk hat bewusst keine Schnittstelle. Das Feld `sevdesk_kontakt_id`
 * existiert in der Datenbank, wird hier aber nicht angeboten: Ein Feld, das
 * nichts bewirkt, wird gepflegt und verwirrt.
 */

import { useMemo, useState } from 'react';
import { Seitenkopf } from '../Rahmen';
import {
  Fehlerkasten,
  Karte,
  Knopf,
  Marker,
  Suchleiste,
  Tabellenkarte,
  Textfeld,
} from '../gestaltung/bausteine';
import { useAbfrage } from '../daten/abfrage';
import { Speicherfehler } from '../daten/speichern';
import { alsKlarfehler, type Klarfehler } from '../daten/fehler';
import { passt } from '../daten/textvergleich';
import { anschriftszeile } from '../daten/anschrift';
import {
  INTERVALLTEXT,
  kundeAendern,
  kundeAnlegen,
  kundenLaden,
  kundeZusammenfuehren,
  type Kunde,
} from '../daten/stammdaten';
import { ZusammenfuehrenFormular } from './Zusammenfuehren';
import { Detailtafel, type Detailfeld } from '../gestaltung/Detailtafel';
import { useFormularOffen, useEntwurfsfeld } from '../daten/formularEntwurf';

/**
 * Die Felder der Detailansicht als Daten, nicht als Formularcode.
 *
 * Kommt später „Zahlungsziel" oder „Ansprechpartner" dazu, ist das ein Eintrag
 * in dieser Liste — kein neuer Bildschirm.
 */
const KUNDENFELDER: Detailfeld[] = [
  { schluessel: 'name', marke: 'Name' },
  /*
   * ═══ WARUM ES EINEN ZWEITEN NAMEN GIBT ═══
   *
   * „Helmut Kälberer GmbH Entsorgung & Containerdienst + Baustoffe &
   * Transporte" gehört so auf die Rechnung. Auf einer Tourkarte im Handy
   * frisst er zwei Zeilen und verdrängt alles, worum es geht — Strecke,
   * Datum, Besetzung.
   *
   * Automatisch abschneiden trifft die falsche Hälfte: Zwei Kunden derselben
   * Firmengruppe sähen danach identisch aus. Wer den Namen kennt, kürzt ihn
   * richtig — also steht er hier als Feld und nicht als Rechenregel.
   */
  {
    schluessel: 'anzeigename',
    marke: 'Anzeigename',
    hinweis:
      'Kurzform für Listen und die App. Leer lassen, wenn der Name ohnehin kurz ist. Auf Rechnungen steht immer der volle Name.',
  },
  { schluessel: 'nummer', marke: 'Kundennummer', hinweis: 'Muss eindeutig sein.' },
  { schluessel: 'zusatz', marke: 'Namenszusatz' },
  { schluessel: 'strasse', marke: 'Straße' },
  { schluessel: 'plz', marke: 'PLZ' },
  { schluessel: 'ort', marke: 'Ort' },
  { schluessel: 'ansprechpartner', marke: 'Ansprechpartner' },
  { schluessel: 'telefon', marke: 'Telefon' },
  { schluessel: 'email', marke: 'E-Mail' },
  {
    schluessel: 'rechnungsintervall',
    marke: 'Rechnung',
    art: 'auswahl',
    optionen: [
      { wert: 'woechentlich', text: 'Wöchentlich' },
      { wert: 'monatlich', text: 'Monatlich' },
      { wert: 'je_auftrag', text: 'Je Auftrag' },
    ],
  },
  { schluessel: 'notiz', marke: 'Notiz' },
];

export function KundenSeite() {
  const liste = useAbfrage(kundenLaden, []);
  const [suche, setSuche] = useState('');
  // Offen bleibt offen — auch über Seitenwechsel und Neuladen (HJ-025).
  const [formularOffen, setFormularOffen] = useFormularOffen('kunde-neu');
  const [zusammen, setZusammen] = useState<Kunde | null>(null);
  const [offen, setOffen] = useState<Kunde | null>(null);
  const [fehler, setFehler] = useState<Klarfehler | null>(null);
  const [beschaeftigt, setBeschaeftigt] = useState<string | null>(null);

  const gefiltert = useMemo(() => {
    const alle = liste.daten ?? [];
    if (!suche.trim()) return alle;
    return alle.filter(
      (k) =>
        passt(k.name, suche) ||
        // Wer die Kurzform sieht, sucht auch nach ihr.
        passt(k.anzeigename ?? '', suche) ||
        passt(k.ort ?? '', suche) ||
        passt(k.nummer ?? '', suche) ||
        k.alt_schreibweisen.some((alt) => passt(alt, suche)),
    );
  }, [liste.daten, suche]);

  async function mitFehlern(schluessel: string, tun: () => Promise<void>) {
    setFehler(null);
    setBeschaeftigt(schluessel);
    try {
      await tun();
    } catch (problem) {
      setFehler(
        problem instanceof Speicherfehler ? problem.klar : alsKlarfehler(problem),
      );
    } finally {
      setBeschaeftigt(null);
    }
  }

  return (
    <>
      <Seitenkopf
        titel="Kunden"
        erklaerung="Die Auftraggeber. Das Rechnungsintervall entscheidet später, wie die Abrechnung bündelt."
        werkzeuge={
          <Knopf art="haupt" onClick={() => setFormularOffen(!formularOffen)}>
            {formularOffen ? 'Abbrechen' : 'Kunde anlegen'}
          </Knopf>
        }
      />

      {fehler && (
        <div style={{ marginBottom: 16 }}>
          <Fehlerkasten fehler={fehler} />
        </div>
      )}

      {offen && (
        <div style={{ marginBottom: 16 }}>
          <Detailtafel
            key={offen.id}
            titel={offen.name}
            felder={KUNDENFELDER}
            laeuft={beschaeftigt === offen.id}
            werte={Object.fromEntries(
              KUNDENFELDER.map((f) => [
                f.schluessel,
                String((offen as unknown as Record<string, unknown>)[f.schluessel] ?? ''),
              ]),
            )}
            hinweise={
              offen.alt_schreibweisen.length > 0 ? (
                <p className="meta">
                  Frühere Schreibweisen: {offen.alt_schreibweisen.join(' · ')}
                </p>
              ) : null
            }
            schliessen={() => setOffen(null)}
            speichern={(geaendert) =>
              void mitFehlern(offen.id, async () => {
                const werte: Record<string, unknown> = {};
                for (const [schluessel, wert] of Object.entries(geaendert)) {
                  werte[schluessel] = wert.trim() === '' ? null : wert.trim();
                }
                await kundeAendern(offen, werte as Partial<Kunde>);
                setOffen(null);
                liste.auffrischen();
              })
            }
          />
        </div>
      )}

      {zusammen && (
        <div style={{ marginBottom: 16 }}>
          {/* Der Schlüssel wirft das Formular beim Wechsel der Quelle weg.
              Sonst bleibt das vorhin gewählte ZIEL stehen, und der nächste
              Kunde wird still in den falschen zusammengeführt. */}
          <ZusammenfuehrenFormular
            key={zusammen.id}
            quelle={zusammen}
            auswahl={liste.daten ?? []}
            wortFuerEinen="Kunden"
            laeuft={beschaeftigt === `merge:${zusammen.id}`}
            abbrechen={() => setZusammen(null)}
            bestaetigen={(zielId) =>
              void mitFehlern(`merge:${zusammen.id}`, async () => {
                await kundeZusammenfuehren(zusammen.id, zielId);
                setZusammen(null);
                liste.auffrischen();
              })
            }
          />
        </div>
      )}

      {formularOffen && (
        <div style={{ marginBottom: 16 }}>
          <AnlegenFormular
            vorhandene={liste.daten ?? []}
            fertig={() => {
              setFormularOffen(false);
              liste.auffrischen();
            }}
          />
        </div>
      )}

      <Tabellenkarte
        titel="Alle Kunden"
        werkzeuge={
          <Suchleiste
            wert={suche}
            setzen={setSuche}
            platzhalter="Name, Ort, Nummer oder alte Schreibweise"
            anzahl={`${gefiltert.length} von ${(liste.daten ?? []).length}`}
          />
        }
        laedt={liste.laedt}
        fehler={liste.fehler}
        nochmal={liste.auffrischen}
        eintraege={gefiltert}
        spalten={[
          { text: 'Name' },
          { text: 'Anschrift' },
          { text: 'Rechnung' },
          { text: 'Status' },
          { text: '', rechts: true },
        ]}
        leer={
          suche ? 'Kein Kunde passt zu dieser Suche.' : 'Noch kein Kunde angelegt.'
        }
        zeile={(kunde) => (
          <tr
            key={kunde.id}
            className={kunde.aktiv ? undefined : 'stillgelegt'}
            aria-current={offen?.id === kunde.id ? 'true' : undefined}
          >
            <td>
              <button className="zeilenlink" onClick={() => setOffen(kunde)}>
                {kunde.anzeigename || kunde.name}
              </button>
              {/* Steht eine Kurzform, gehört der volle Name trotzdem sichtbar —
                  sonst sucht jemand nach dem Namen auf der Rechnung und findet
                  ihn in dieser Liste nicht wieder. */}
              {kunde.anzeigename && <div className="meta">{kunde.name}</div>}
              {kunde.nummer && <div className="meta">Nr. {kunde.nummer}</div>}
              {kunde.alt_schreibweisen.length > 0 && (
                <div className="meta">
                  früher: {kunde.alt_schreibweisen.join(' · ')}
                </div>
              )}
            </td>
            <td>
              {[kunde.strasse, [kunde.plz, kunde.ort].filter(Boolean).join(' ')]
                .filter(Boolean)
                .join(', ') || '—'}
            </td>
            <td>
              <select
                value={kunde.rechnungsintervall}
                disabled={beschaeftigt === kunde.id}
                onChange={(e) =>
                  void mitFehlern(kunde.id, async () => {
                    await kundeAendern(kunde, {
                      rechnungsintervall: e.target
                        .value as Kunde['rechnungsintervall'],
                    });
                    liste.auffrischen();
                  })
                }
              >
                {(Object.keys(INTERVALLTEXT) as Kunde['rechnungsintervall'][]).map(
                  (i) => (
                    <option key={i} value={i}>
                      {INTERVALLTEXT[i]}
                    </option>
                  ),
                )}
              </select>
            </td>
            <td>
              <div className="reihe" style={{ gap: 6 }}>
                {!kunde.bestaetigt && <Marker art="warn">unbestätigt</Marker>}
                {!kunde.aktiv && <Marker art="neutral">stillgelegt</Marker>}
                {kunde.bestaetigt && kunde.aktiv && (
                  <Marker art="gut">geprüft</Marker>
                )}
              </div>
            </td>
            <td className="tabelle__rechts">
              <div className="reihe reihe--rechts">
                {!kunde.bestaetigt && (
                  <Knopf
                    art="still"
                    klein
                    disabled={beschaeftigt === kunde.id}
                    onClick={() =>
                      void mitFehlern(kunde.id, async () => {
                        await kundeAendern(kunde, { bestaetigt: true });
                        liste.auffrischen();
                      })
                    }
                  >
                    Bestätigen
                  </Knopf>
                )}
                <Knopf art="still" klein onClick={() => setZusammen(kunde)}>
                  Zusammenführen
                </Knopf>
                <Knopf
                  art="still"
                  klein
                  disabled={beschaeftigt === kunde.id}
                  onClick={() =>
                    void mitFehlern(kunde.id, async () => {
                      await kundeAendern(kunde, { aktiv: !kunde.aktiv });
                      liste.auffrischen();
                    })
                  }
                >
                  {kunde.aktiv ? 'Stilllegen' : 'Zurückholen'}
                </Knopf>
              </div>
            </td>
          </tr>
        )}
      />
    </>
  );
}

function AnlegenFormular(eigenschaften: {
  vorhandene: Kunde[];
  fertig: () => void;
}) {
  const [name, setName] = useEntwurfsfeld('kunde-neu', 'name', '');
  const [nummer, setNummer] = useEntwurfsfeld('kunde-neu', 'nummer', '');
  const [strasse, setStrasse] = useEntwurfsfeld('kunde-neu', 'strasse', '');
  const [plz, setPlz] = useEntwurfsfeld('kunde-neu', 'plz', '');
  const [ort, setOrt] = useEntwurfsfeld('kunde-neu', 'ort', '');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Klarfehler | null>(null);

  const aehnliche = useMemo(() => {
    if (name.trim().length < 3) return [];
    return eigenschaften.vorhandene.filter((k) => passt(k.name, name)).slice(0, 4);
  }, [eigenschaften.vorhandene, name]);

  async function speichern() {
    if (!name.trim() || laeuft) return;
    setLaeuft(true);
    setFehler(null);
    try {
      await kundeAnlegen({ name, nummer, strasse, plz, ort });
      eigenschaften.fertig();
    } catch (problem) {
      setFehler(
        problem instanceof Speicherfehler ? problem.klar : alsKlarfehler(problem),
      );
      setLaeuft(false);
    }
  }

  return (
    <Karte titel="Neuer Kunde">
      <div className="stapel">
        {fehler && <Fehlerkasten fehler={fehler} />}
        <div className="feldreihe">
          <Textfeld marke="Name" wert={name} setzen={setName} autoFocus />
          <Textfeld
            marke="Kundennummer"
            wert={nummer}
            setzen={setNummer}
            hinweis="Optional, muss aber eindeutig sein."
          />
          <Textfeld marke="Straße" wert={strasse} setzen={setStrasse} />
          <Textfeld marke="PLZ" wert={plz} setzen={setPlz} />
          <Textfeld marke="Ort" wert={ort} setzen={setOrt} />
        </div>

        {aehnliche.length > 0 && (
          <div className="meldung meldung--warnung">
            <div>
              <strong>Ähnliche Einträge gefunden – prüfe, ob es ihn schon gibt.</strong>
              {aehnliche
                .map((k) => {
                  const anschrift = anschriftszeile(k.strasse, k.plz, k.ort);
                  return anschrift ? `${k.name} (${anschrift})` : k.name;
                })
                .join(' · ')}
            </div>
          </div>
        )}

        <div className="reihe reihe--rechts">
          <Knopf art="still" onClick={eigenschaften.fertig}>
            Abbrechen
          </Knopf>
          <Knopf
            art="haupt"
            disabled={!name.trim() || laeuft}
            onClick={() => void speichern()}
          >
            {laeuft
              ? 'Wird angelegt …'
              : aehnliche.length > 0
                ? 'Ist neu – anlegen'
                : 'Anlegen'}
          </Knopf>
        </div>
      </div>
    </Karte>
  );
}
